import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import GIF from 'gif.js';
import { 
  Camera, 
  Upload, 
  X, 
  Trash2, 
  GripVertical, 
  AlertCircle, 
  CheckCircle,
  Loader,
  MessageSquare,
  Undo2,
  Edit,
  Save,
  XCircle,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ExternalLink,
  Play,
  Video,
  ImagePlus
} from 'lucide-react';
import { 
  uploadEquipmentPhotos, 
  deleteEquipmentPhoto, 
  getEquipmentPhotoURL,
  listEquipmentPhotos,
  resizeImageFile,
  PHOTO_CONSTANTS 
} from '../lib/photoStorageService';

const EquipmentPhotoUpload = ({ 
  orderId, 
  equipmentId, 
  existingPhotos = [], 
  onPhotosChange, 
  canEdit = true,
  onSaveComplete = null, // Prop to signal when save is complete
  onHasChanges = null, // Prop to notify parent about unsaved changes
  onCancelRef = null, // Ref to expose cancel function to parent
  onSave = null, // Callback to trigger parent's save functionality
  onEditModeChange = null // Callback to notify parent about edit mode changes
}) => {
  const [photos, setPhotos] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUserActions, setHasUserActions] = useState(false);
  const [newlyUploadedPhotos, setNewlyUploadedPhotos] = useState([]); // Track photos uploaded in this session
  const [allUploadedPhotoIds, setAllUploadedPhotoIds] = useState([]); // Track ALL uploaded photo IDs for cleanup (including intermediate uploads)
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [resizingFiles, setResizingFiles] = useState([]);
  const [convertingVideos, setConvertingVideos] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPinchDistance, setLastPinchDistance] = useState(0);
  const [isPinching, setIsPinching] = useState(false);
  const [loadedVideos, setLoadedVideos] = useState(new Set());
  
  
  
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const newlyUploadedPhotosRef = useRef([]);
  const allUploadedPhotoIdsRef = useRef([]);
  const photosRef = useRef([]);
  const existingPhotosRef = useRef([]);
  const galleryVideoRef = useRef(null);
  const originalExistingPhotosRef = useRef([]); // Store original state for comparison
  const onPhotosChangeRef = useRef(onPhotosChange);

  // Keep refs in sync with state
  useEffect(() => {
    newlyUploadedPhotosRef.current = newlyUploadedPhotos;
  }, [newlyUploadedPhotos]);

  useEffect(() => {
    allUploadedPhotoIdsRef.current = allUploadedPhotoIds;
  }, [allUploadedPhotoIds]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    existingPhotosRef.current = existingPhotos;
    
    // Capture the original existing photos on first load (when no user actions yet)
    if (!hasUserActions && existingPhotos && originalExistingPhotosRef.current.length === 0) {
      originalExistingPhotosRef.current = existingPhotos.map(photo => ({
        id: photo.id || photo.photoId,
        comment: photo.comment || ''
      }));
    }
  }, [existingPhotos, hasUserActions]);

  // Auto-play videos in gallery when opened or when switching between photos
  useEffect(() => {
    if (galleryOpen && galleryVideoRef.current && isVideoFile(photos[currentPhotoIndex]?.filename)) {
      const video = galleryVideoRef.current;
      const playVideo = () => {
        video.play().catch((error) => {
          console.log('Auto-play blocked by browser policy:', error);
        });
      };
      
      // Try to play immediately
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        playVideo();
      } else {
        // Wait for video to load enough data
        video.addEventListener('loadeddata', playVideo, { once: true });
      }
    }
  }, [galleryOpen, currentPhotoIndex, photos]);

  // Keep onPhotosChange ref in sync
  useEffect(() => {
    onPhotosChangeRef.current = onPhotosChange;
  }, [onPhotosChange]);

  // Helper function to format file size
  const formatFileSize = useCallback((sizeInBytes) => {
    if (!sizeInBytes || sizeInBytes === 0) return '';
    
    const sizeInKB = sizeInBytes / 1024;
    if (sizeInKB >= 1000) {
      const sizeInMB = sizeInKB / 1024;
      return `${sizeInMB.toFixed(1)} MB`;
    }
    return `${sizeInKB.toFixed(1)} KB`;
  }, []);

  // Helper function to check if file is a video
  const isVideoFile = useCallback((filename) => {
    if (!filename) return false;
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension === 'webm' || extension === 'mp4' || extension === 'mov' || extension === 'avi';
  }, []);

  // Helper function to convert video to silent WebM (30fps, video quality)
  const convertVideoToWebM = useCallback(async (videoFile) => {
    console.log('Starting video to silent WebM conversion for:', videoFile.name);
    
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      
      // Set up video element
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.crossOrigin = 'anonymous';
      
      const timeout = setTimeout(() => {
        console.error('Video conversion timeout');
        reject(new Error('Tiempo de conversión agotado - el video puede estar corrupto o no ser compatible'));
      }, 120000); // 120 second timeout
      
      video.onloadedmetadata = async () => {
        try {
          console.log('Video metadata loaded:', {
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight
          });
          
          // Smart resolution scaling based on file size requirements
          let { videoWidth, videoHeight } = video;
          
          if (videoWidth === 0 || videoHeight === 0) {
            clearTimeout(timeout);
            reject(new Error('Invalid video dimensions'));
            return;
          }
          
          // Ensure dimensions are even (required for some codecs)
          videoWidth = videoWidth % 2 === 0 ? videoWidth : videoWidth - 1;
          videoHeight = videoHeight % 2 === 0 ? videoHeight : videoHeight - 1;
          
          // Dynamic resolution scaling based on duration and target file size
          const targetFileSizeMB = 3.0;
          const estimatedBitsPerPixel = 0.15; // Conservative estimate for WebM
          const maxPixelsForSize = (targetFileSizeMB * 1024 * 1024 * 8) / (video.duration * 30 * estimatedBitsPerPixel);
          
          const currentPixels = videoWidth * videoHeight;
          
          if (currentPixels > maxPixelsForSize) {
            // Scale down to fit size requirements
            const scaleFactor = Math.sqrt(maxPixelsForSize / currentPixels);
            videoWidth = Math.floor(videoWidth * scaleFactor);
            videoHeight = Math.floor(videoHeight * scaleFactor);
            
            // Ensure scaled dimensions are even and reasonable
            videoWidth = Math.max(320, videoWidth % 2 === 0 ? videoWidth : videoWidth - 1);
            videoHeight = Math.max(240, videoHeight % 2 === 0 ? videoHeight : videoHeight - 1);
            
            console.log(`Scaled resolution from ${video.videoWidth}x${video.videoHeight} to ${videoWidth}x${videoHeight} for size optimization`);
          }
          
          // Additional caps for very large resolutions
          const maxWidth = 1280;  // Max 1280p width for reasonable file size
          const maxHeight = 720;  // Max 720p height
          
          if (videoWidth > maxWidth || videoHeight > maxHeight) {
            const ratio = Math.min(maxWidth / videoWidth, maxHeight / videoHeight);
            videoWidth = Math.floor(videoWidth * ratio);
            videoHeight = Math.floor(videoHeight * ratio);
            
            // Ensure scaled dimensions are even
            videoWidth = videoWidth % 2 === 0 ? videoWidth : videoWidth - 1;
            videoHeight = videoHeight % 2 === 0 ? videoHeight : videoHeight - 1;
          }
          
          // Preserve more of the original duration while keeping under size limit
          let maxDuration = Math.min(video.duration, 15); // Keep to 15 seconds as specified
          
          // Adjust duration more conservatively to maintain quality
          const pixelCount = videoWidth * videoHeight;
          if (pixelCount > 2000000) { // > 2MP (very high resolution)
            maxDuration = Math.min(maxDuration, 12);
          } else if (pixelCount > 1000000) { // > 1MP
            maxDuration = Math.min(maxDuration, 15);
          }
          // For smaller resolutions, allow full 15 seconds
          
          console.log(`Creating silent WebM video: ${videoWidth}x${videoHeight}, ${maxDuration}s`);
          
          // Try conversion with multiple quality levels if needed
          let webmBlob = null;
          let attempts = 0;
          const maxAttempts = 3;
          const maxFileSize = 3.2 * 1024 * 1024; // 3.2MB limit
          
          while (attempts < maxAttempts && !webmBlob) {
            attempts++;
            
            // Adjust quality/resolution for subsequent attempts
            let currentWidth = videoWidth;
            let currentHeight = videoHeight;
            let currentDuration = maxDuration;
            
            if (attempts === 2) {
              // Second attempt: reduce resolution by 25%
              currentWidth = Math.floor(videoWidth * 0.75);
              currentHeight = Math.floor(videoHeight * 0.75);
              currentWidth = currentWidth % 2 === 0 ? currentWidth : currentWidth - 1;
              currentHeight = currentHeight % 2 === 0 ? currentHeight : currentHeight - 1;
              console.log(`Attempt ${attempts}: Reducing resolution to ${currentWidth}x${currentHeight}`);
            } else if (attempts === 3) {
              // Third attempt: reduce resolution by 50% and duration slightly
              currentWidth = Math.floor(videoWidth * 0.5);
              currentHeight = Math.floor(videoHeight * 0.5);
              currentWidth = Math.max(320, currentWidth % 2 === 0 ? currentWidth : currentWidth - 1);
              currentHeight = Math.max(240, currentHeight % 2 === 0 ? currentHeight : currentHeight - 1);
              currentDuration = Math.min(maxDuration, 12); // Slightly shorter if needed
              console.log(`Attempt ${attempts}: Reducing resolution to ${currentWidth}x${currentHeight} and duration to ${currentDuration}s`);
            }
            
            try {
              const attemptBlob = await createSilentWebM(video, currentWidth, currentHeight, currentDuration);
              
              console.log(`Attempt ${attempts} result: ${(attemptBlob.size / (1024 * 1024)).toFixed(2)}MB`);
              
              if (attemptBlob.size <= maxFileSize) {
                webmBlob = attemptBlob;
                maxDuration = currentDuration; // Update final duration
                console.log(`WebM created successfully on attempt ${attempts}, size: ${(attemptBlob.size / (1024 * 1024)).toFixed(2)}MB`);
              } else if (attempts === maxAttempts) {
                // Last attempt failed, use it anyway but warn
                console.warn(`All attempts exceeded size limit. Using final attempt: ${(attemptBlob.size / (1024 * 1024)).toFixed(2)}MB`);
                webmBlob = attemptBlob;
                maxDuration = currentDuration;
              }
            } catch (error) {
              console.warn(`Attempt ${attempts} failed:`, error.message);
              if (attempts === maxAttempts) {
                throw error; // Re-throw on final attempt
              }
            }
          }
          
          clearTimeout(timeout);
          
          resolve({
            blob: webmBlob,
            type: 'video/webm',
            duration: maxDuration
          });
          
        } catch (error) {
          clearTimeout(timeout);
          reject(new Error('Error during WebM creation: ' + error.message));
        }
      };
      
      video.onerror = (error) => {
        clearTimeout(timeout);
        console.error('Video loading error:', error);
        reject(new Error('Error loading video file'));
      };
      
      try {
        video.src = URL.createObjectURL(videoFile);
        video.load();
      } catch (error) {
        clearTimeout(timeout);
        reject(new Error('Error creating video URL: ' + error.message));
      }
    });
  }, []);

  // Helper function to create silent WebM video with improved quality and reliability
  const createSilentWebM = useCallback(async (video, width, height, duration) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;
      
      // High quality canvas settings
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Get canvas stream for MediaRecorder with consistent frame rate
      const targetFPS = 30;
      const stream = canvas.captureStream(targetFPS);
      
      // Dynamic bitrate calculation based on resolution and duration to stay under 3.2MB
      const targetFileSizeMB = 3.0; // Target 3MB to leave buffer for WebM overhead
      const targetFileSizeBytes = targetFileSizeMB * 1024 * 1024;
      const pixelCount = width * height;
      
      // Calculate optimal bitrate to stay under size limit
      // Formula: (target_size_bytes * 8) / duration_seconds = bits_per_second
      let optimalBitrate = Math.floor((targetFileSizeBytes * 8) / duration);
      
      // Adjust bitrate based on resolution complexity
      if (pixelCount > 2000000) { // > 2MP - Very high resolution
        optimalBitrate = Math.min(optimalBitrate, 2000000); // Max 2 Mbps
      } else if (pixelCount > 1000000) { // > 1MP - High resolution  
        optimalBitrate = Math.min(optimalBitrate, 2500000); // Max 2.5 Mbps
      } else if (pixelCount > 500000) { // > 0.5MP - Medium resolution
        optimalBitrate = Math.min(optimalBitrate, 3000000); // Max 3 Mbps
      } else { // Low resolution
        optimalBitrate = Math.min(optimalBitrate, 3500000); // Max 3.5 Mbps
      }
      
      // Ensure minimum quality
      optimalBitrate = Math.max(optimalBitrate, 800000); // Min 0.8 Mbps
      
      console.log(`Video conversion: ${width}x${height} (${pixelCount} pixels), ${duration}s, target bitrate: ${(optimalBitrate/1000000).toFixed(1)} Mbps`);
      
      // Enhanced MediaRecorder configuration with calculated bitrate
      let mimeType = 'video/webm;codecs=vp9';
      let bitrate = optimalBitrate;
      
      // Fallback to VP8 if VP9 not supported (VP8 typically produces smaller files)
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        // VP8 is less efficient, so reduce bitrate slightly
        bitrate = Math.floor(optimalBitrate * 0.9);
      }
      
      // Final fallback to default WebM
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
        bitrate = Math.floor(optimalBitrate * 0.85);
      }
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: bitrate,
        audioBitsPerSecond: 0 // Explicitly disable audio to save space
      });
      
      const chunks = [];
      let isRecording = false;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        isRecording = false;
        const webmBlob = new Blob(chunks, { type: 'video/webm' });
        resolve(webmBlob);
      };
      
      mediaRecorder.onerror = (error) => {
        isRecording = false;
        reject(new Error('MediaRecorder error: ' + error.message));
      };
      
      // Improved frame rendering with precise timing to prevent speed-up
      const fps = targetFPS;
      const frameDuration = 1 / fps;
      let currentFrame = 0;
      const totalFrames = Math.ceil(duration * fps);
      let renderingComplete = false;
      let startTime = null;
      
      const renderFrame = async () => {
        if (renderingComplete || currentFrame >= totalFrames) {
          if (isRecording) {
            renderingComplete = true;
            mediaRecorder.stop();
          }
          return;
        }
        
        // Track real time to maintain proper duration
        if (startTime === null) {
          startTime = performance.now();
        }
        
        const targetTime = Math.min(currentFrame * frameDuration, duration - 0.01);
        
        try {
          // More precise seeking to prevent frame skipping
          await new Promise((resolveSeek, rejectSeek) => {
            let seekResolved = false;
            
            const seekHandler = () => {
              if (!seekResolved) {
                seekResolved = true;
                video.removeEventListener('seeked', seekHandler);
                resolveSeek();
              }
            };
            
            const errorHandler = () => {
              if (!seekResolved) {
                seekResolved = true;
                video.removeEventListener('seeked', seekHandler);
                video.removeEventListener('error', errorHandler);
                console.warn(`Seek error at ${targetTime}s, skipping frame`);
                resolveSeek(); // Continue with next frame
              }
            };
            
            video.addEventListener('seeked', seekHandler);
            video.addEventListener('error', errorHandler);
            
            // Set video time more precisely
            if (Math.abs(video.currentTime - targetTime) > 0.05) {
              video.currentTime = targetTime;
            } else {
              // Already at correct time
              seekResolved = true;
              video.removeEventListener('seeked', seekHandler);
              video.removeEventListener('error', errorHandler);
              resolveSeek();
            }
            
            // Longer timeout for seek operation to ensure accuracy
            setTimeout(() => {
              if (!seekResolved) {
                seekResolved = true;
                video.removeEventListener('seeked', seekHandler);
                video.removeEventListener('error', errorHandler);
                resolveSeek();
              }
            }, 200);
          });
          
          // Clear and draw frame with enhanced quality settings
          ctx.save();
          ctx.clearRect(0, 0, width, height);
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(video, 0, 0, width, height);
          ctx.restore();
          
          currentFrame++;
          
          // Use requestAnimationFrame for better timing consistency
          if (!renderingComplete) {
            // Calculate next frame timing to maintain exact duration
            const expectedTime = startTime + (currentFrame * (1000 / fps));
            const currentTime = performance.now();
            const delay = Math.max(0, expectedTime - currentTime);
            
            setTimeout(() => {
              requestAnimationFrame(renderFrame);
            }, delay);
          }
          
        } catch (error) {
          console.warn('Frame rendering error:', error);
          currentFrame++; // Skip problematic frame
          if (!renderingComplete) {
            setTimeout(() => {
              requestAnimationFrame(renderFrame);
            }, 1000 / fps);
          }
        }
      };
      
      // Start recording and rendering with better synchronization
      mediaRecorder.start();
      isRecording = true;
      
      // Wait for video to be fully ready, then start with precise timing
      const startRendering = () => {
        // Ensure video is at start position
        video.currentTime = 0;
        setTimeout(() => {
          renderFrame();
        }, 100); // Small delay to ensure video is ready
      };
      
      video.addEventListener('canplay', startRendering, { once: true });
      
      // Enhanced timeout based on video duration and processing complexity
      const timeoutDuration = Math.max(duration * 1000 + 30000, 45000); // At least 45s for complex videos
      setTimeout(() => {
        if (isRecording && !renderingComplete) {
          renderingComplete = true;
          mediaRecorder.stop();
          reject(new Error('Conversion timeout - video may be too complex'));
        }
      }, timeoutDuration);
      
      // Ensure video is ready with better state checking
      if (video.readyState >= 3) { // HAVE_FUTURE_DATA
        startRendering();
      } else {
        video.load();
      }
    });
  }, []);

  // Helper function to capture a single frame from video
  const captureVideoFrame = useCallback((video, canvas, ctx, time, width, height) => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        resolve(null); // Don't reject, just return null to skip this frame
      }, 5000); // 5 second timeout per frame
      
      const onSeeked = () => {
        clearTimeout(timeout);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        
        try {
          // Use high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(video, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            if (blob && blob.size > 0) {
              resolve(blob);
            } else {
              resolve(null);
            }
          }, 'image/png', 1.0); // Use maximum quality for frame capture
        } catch (error) {
          console.error('Error capturing frame:', error);
          resolve(null);
        }
      };
      
      const onError = (error) => {
        clearTimeout(timeout);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        console.error('Video seek error:', error);
        resolve(null);
      };
      
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('error', onError);
      
      try {
        video.currentTime = time;
      } catch (error) {
        clearTimeout(timeout);
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('error', onError);
        resolve(null);
      }
    });
  }, []);

  // Helper function to create animated GIF from frames using gif.js
  const createAnimatedGIF = useCallback(async (frames, delay, width, height, progressItemId = null) => {
    return new Promise((resolve, reject) => {
      if (frames.length === 0) {
        reject(new Error('No frames to create GIF'));
        return;
      }
      
      console.log(`Creating animated GIF with ${frames.length} frames, delay: ${delay}ms`);
      
      // Create GIF with gif.js - highest quality settings
      const gif = new GIF({
        workers: 1, // Use 1 worker but specify the correct path
        workerScript: '/gif.worker.js', // Explicit path from public folder
        quality: 1, // Highest quality (1-30, lower = better quality)
        width: width,
        height: height,
        repeat: 0, // Loop forever (0 = infinite loop)
        debug: false, // Disable debug to reduce console spam
        dither: 'FloydSteinberg', // Enable dithering for better quality
        transparent: null,
        background: '#fff',
        globalPalette: false, // Use individual palettes for better color accuracy
        disposalMethod: 2, // Restore to background color between frames
        sample: 1 // Use all pixels for color quantization (highest quality)
      });
      
      // Convert frames to ImageData and add to GIF
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;
      
      // Set high quality rendering for GIF frames
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      let processedFrames = 0;
      
      // Add a timeout for the GIF rendering process
      const renderTimeout = setTimeout(() => {
        reject(new Error('Tiempo agotado durante la creación del GIF'));
      }, 120000); // 120 seconds for rendering without workers
      
      const processNextFrame = async (index) => {
        if (index >= frames.length) {
          // All frames processed, render GIF
          console.log('All frames processed, starting GIF render...');
          gif.render();
          return;
        }
        
        try {
          const frameBlob = frames[index];
          const img = new Image();
          
          const frameTimeout = setTimeout(() => {
            console.warn(`Frame ${index} processing timeout`);
            processNextFrame(index + 1); // Skip this frame
          }, 10000); // 10 second timeout per frame
          
          img.onload = () => {
            clearTimeout(frameTimeout);
            try {
              // Draw image to canvas and get ImageData
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(img, 0, 0, width, height);
              
              // Add frame to GIF with proper animation settings
              gif.addFrame(canvas, { 
                delay: delay,
                copy: true // Ensure frame is copied properly
              });
              
              processedFrames++;
              console.log(`Processed frame ${processedFrames}/${frames.length}, delay: ${delay}ms`);
              
              // Clean up the image URL
              URL.revokeObjectURL(img.src);
              
              // Process next frame with a small delay to prevent blocking
              setTimeout(() => processNextFrame(index + 1), 10);
              
            } catch (error) {
              console.error('Error processing frame:', error);
              URL.revokeObjectURL(img.src);
              processNextFrame(index + 1); // Skip this frame
            }
          };
          
          img.onerror = () => {
            clearTimeout(frameTimeout);
            console.error('Error loading frame image');
            URL.revokeObjectURL(img.src);
            processNextFrame(index + 1); // Skip this frame
          };
          
          img.src = URL.createObjectURL(frameBlob);
          
        } catch (error) {
          console.error('Error processing frame:', error);
          processNextFrame(index + 1); // Skip this frame
        }
      };
      
      gif.on('finished', (blob) => {
        clearTimeout(renderTimeout);
        console.log('GIF creation finished, size:', blob.size);
        resolve(blob);
      });
      
      gif.on('abort', () => {
        clearTimeout(renderTimeout);
        reject(new Error('GIF creation was aborted'));
      });
      
      gif.on('progress', (progress) => {
        const percent = Math.round(progress * 100);
        console.log('GIF creation progress:', percent + '%');
        // Update conversion progress in UI if ID provided
        if (progressItemId) {
          setConvertingVideos(prev => prev.map(item => 
            item.id === progressItemId 
              ? { ...item, progress: 75 + (percent * 0.25), status: 'rendering' }
              : item
          ));
        }
      });
      
      // Add error handler for gif.js
      gif.on('error', (error) => {
        clearTimeout(renderTimeout);
        console.error('GIF creation error:', error);
        reject(new Error('Error durante la creación del GIF: ' + (error.message || error)));
      });
      
      // Start processing frames
      processNextFrame(0);
    });
  }, []);


  // Initialize photos from existing photos (initialization only, no callbacks)
  useEffect(() => {
    // Don't override if user has performed actions (uploads, deletions, etc)
    if (hasUserActions) {
      return;
    }
    
    if (existingPhotos && existingPhotos.length > 0) {
      const formattedPhotos = existingPhotos.map((photo, index) => ({
        id: photo.photoId || photo.id || `existing-${index}`,
        photoId: photo.photoId || photo.id,
        downloadURL: photo.downloadURL,
        comment: photo.comment || '',
        uploaded: true,
        size: photo.fileSize || photo.size || 0,
        filename: photo.fileName || photo.filename || photo.photoId,
        uploadedAt: photo.uploadedAt || new Date().toISOString(),
        markedForDeletion: photo.markedForDeletion || false
      }));
      
      setPhotos(formattedPhotos);
      
    } else if (existingPhotos && existingPhotos.length === 0) {
      setPhotos([]);
    }
  }, [existingPhotos]);

  // Handle save completion - reset hasUserActions and sync with existingPhotos
  const lastSaveCompletionRef = useRef(null);
  useEffect(() => {
    // Only clear if onSaveComplete has a NEW timestamp (indicating save completed)
    if (onSaveComplete && typeof onSaveComplete === 'number' && onSaveComplete !== lastSaveCompletionRef.current) {
      lastSaveCompletionRef.current = onSaveComplete;
      
      setHasUserActions(false);
      setIsEditMode(false);
      setNewlyUploadedPhotos([]); // Clear newly uploaded photos after save
      setAllUploadedPhotoIds([]); // Clear all uploaded photo IDs after save
      
      // Update original photos ref to new saved state
      if (existingPhotos && existingPhotos.length >= 0) {
        originalExistingPhotosRef.current = existingPhotos.map(photo => ({
          id: photo.id || photo.photoId,
          comment: photo.comment || ''
        }));
        
        const formattedPhotos = existingPhotos.map((photo, index) => ({
          id: photo.photoId || photo.id || `existing-${index}`,
          photoId: photo.photoId || photo.id,
          downloadURL: photo.downloadURL,
          comment: photo.comment || '',
          uploaded: true,
          size: photo.fileSize || photo.size || 0,
          filename: photo.fileName || photo.filename || photo.photoId,
          uploadedAt: photo.uploadedAt || new Date().toISOString(),
          markedForDeletion: photo.markedForDeletion || false
        }));
        
        setPhotos(formattedPhotos);
      }
    }
  }, [onSaveComplete, existingPhotos]);

  // Load existing photos from Firebase Storage (fallback only if no existingPhotos)
  const loadExistingPhotos = useCallback(async () => {
    if (!orderId || !equipmentId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Try to load from Firebase Storage as fallback
      const storagePhotos = await listEquipmentPhotos(orderId, equipmentId);
      
      if (storagePhotos.length > 0) {
        const formattedPhotos = storagePhotos.map((photo, index) => ({
          id: photo.photoId || `loaded-${index}`,
          photoId: photo.photoId,
          downloadURL: photo.downloadURL,
          comment: '', // Comments would need to be stored separately in Firestore
          uploaded: true,
          size: 0,
          filename: photo.photoId,
          uploadedAt: new Date().toISOString()
        }));
        
        setPhotos(formattedPhotos);
        // Use setTimeout to defer the callback and avoid setState during render
        setTimeout(() => {
          if (onPhotosChangeRef.current) {
            onPhotosChangeRef.current(formattedPhotos);
          }
        }, 0);
      } else {
        // No photos found, set empty state
        setPhotos([]);
        // Use setTimeout to defer the callback and avoid setState during render
        setTimeout(() => {
          if (onPhotosChangeRef.current) {
            onPhotosChangeRef.current([]);
          }
        }, 0);
      }
    } catch (error) {
      console.error('Error loading existing photos:', error);
      // Don't show error for empty storage, just set empty state
      setPhotos([]);
      // Use setTimeout to defer the callback and avoid setState during render
      setTimeout(() => {
        if (onPhotosChangeRef.current) {
          onPhotosChangeRef.current([]);
        }
      }, 0);
      
      // Only show error if it's not a "not found" error
      if (!error.message.includes('does not exist') && !error.message.includes('unauthorized')) {
        setError('Error al cargar las fotos existentes');
        setTimeout(() => setError(''), 5000);
      }
    } finally {
      setLoading(false);
    }
  }, [orderId, equipmentId]);

  // Initialize component - only load from storage if no existingPhotos are provided at all
  useEffect(() => {
    // If existingPhotos prop is provided (even if empty), use it and don't load from storage
    if (existingPhotos !== undefined) {
      setLoading(false);
      return;
    }
    
    // Only if no existingPhotos prop is provided at all, try loading from Firebase Storage
    if (orderId && equipmentId) {
      loadExistingPhotos();
    } else {
      setLoading(false);
    }
  }, [orderId, equipmentId, loadExistingPhotos]); // Include necessary dependencies

  // Handle changes to existingPhotos prop
  useEffect(() => {
    // Always stop loading when existingPhotos prop changes
    // This includes when it changes from undefined to an empty array
    setLoading(false);
  }, [existingPhotos]);


  // Stable cancel function that uses refs to avoid stale closures
  const handleCancelStable = useCallback(async () => {
    try {
      const currentNewlyUploaded = newlyUploadedPhotosRef.current;
      const currentAllUploadedIds = allUploadedPhotoIdsRef.current;
      const currentPhotos = photosRef.current;
      const currentExisting = existingPhotosRef.current;
      
      
      // Delete ALL uploaded photos from storage (including any intermediate uploads)
      for (const photoId of currentAllUploadedIds) {
        if (photoId && orderId && equipmentId) {
          try {
            await deleteEquipmentPhoto(orderId, equipmentId, photoId);
          } catch (error) {
            console.warn(`Could not delete uploaded photo ${photoId}:`, error.message);
          }
        } else {
          console.warn(`Missing required data for photo deletion:`, { photoId, orderId, equipmentId });
        }
      }
      
      // Remove newly uploaded photos from current photos state
      const newlyUploadedIds = new Set(currentNewlyUploaded.map(p => p.id));
      const photosWithoutNewlyUploaded = currentPhotos.filter(photo => !newlyUploadedIds.has(photo.id));
      
      // Reset deletion flags and comments for remaining photos
      const revertedPhotos = photosWithoutNewlyUploaded.map(photo => {
        const originalPhoto = originalExistingPhotosRef.current.find(ep => ep.id === photo.id);
        return {
          ...photo,
          comment: originalPhoto?.comment || '', // Revert to original comment
          markedForDeletion: false // Reset deletion flags
        };
      });
      
      
      // Update UI state
      setPhotos(revertedPhotos);
      setNewlyUploadedPhotos([]);
      setAllUploadedPhotoIds([]); // Clear all uploaded photo IDs
      setHasUserActions(false);
      setError('');
      
      // Notify parent of revert
      if (onPhotosChangeRef.current) {
        onPhotosChangeRef.current(revertedPhotos);
      }
    } catch (error) {
      console.error('Error during cancel operation:', error);
      setError('Error al cancelar los cambios');
      setTimeout(() => setError(''), 5000);
    }
  }, [orderId, equipmentId]);

  // Handle save functionality - calls parent's save logic
  const handleSave = useCallback(async () => {
    // Trigger parent's save logic first
    if (onSave) {
      try {
        await onSave();
        // Only exit edit mode if save was successful
        setIsEditMode(false);
        setHasUserActions(false);
      } catch (error) {
        console.error('Save failed:', error);
        // Stay in edit mode if save failed
      }
    } else {
      // Fallback: just exit edit mode if no save callback
      setIsEditMode(false);
      setHasUserActions(false);
    }
  }, [onSave]);

  // Handle cancel functionality
  const handleCancel = useCallback(async () => {
    await handleCancelStable();
    setIsEditMode(false);
  }, [handleCancelStable]);

  // Calculate if there are changes
  const hasChanges = useMemo(() => {
    if (!hasUserActions) {
      return false;
    }
    
    const hasNewUploads = newlyUploadedPhotos.length > 0;
    const hasDeletions = photos.some(photo => photo.markedForDeletion);
    const hasCommentChanges = photos.some(photo => {
      const originalPhoto = originalExistingPhotosRef.current.find(ep => ep.id === photo.id);
      return photo.comment !== (originalPhoto?.comment || '');
    });
    
    return hasNewUploads || hasDeletions || hasCommentChanges;
  }, [
    hasUserActions, 
    newlyUploadedPhotos.length,
    JSON.stringify(photos.map(p => ({ id: p.id, markedForDeletion: p.markedForDeletion, comment: p.comment })))
  ]);

  // Notify parent about changes
  const onHasChangesRef = useRef(onHasChanges);
  onHasChangesRef.current = onHasChanges;
  
  useEffect(() => {
    if (onHasChangesRef.current) {
      onHasChangesRef.current(hasChanges);
    }
  }, [hasChanges]);

  // Notify parent about edit mode changes
  const onEditModeChangeRef = useRef(onEditModeChange);
  onEditModeChangeRef.current = onEditModeChange;
  
  useEffect(() => {
    if (onEditModeChangeRef.current) {
      onEditModeChangeRef.current(isEditMode);
    }
  }, [isEditMode]);

  // Expose cancel function to parent via ref
  useEffect(() => {
    if (onCancelRef && typeof onCancelRef === 'object') {
      onCancelRef.current = handleCancelStable;
    }
  }, [onCancelRef, handleCancelStable]);

  // Gallery handlers
  const openGallery = useCallback((photoIndex) => {
    setCurrentPhotoIndex(photoIndex);
    setGalleryOpen(true);
  }, []);

  const closeGallery = useCallback(() => {
    setGalleryOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  const goToNext = useCallback(() => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  const openInNewTab = useCallback(() => {
    const currentPhoto = photos[currentPhotoIndex];
    if (currentPhoto?.downloadURL) {
      window.open(currentPhoto.downloadURL, '_blank');
    }
  }, [photos, currentPhotoIndex]);

  // Reset zoom and position when photo changes
  useEffect(() => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
    setIsDragging(false);
    setIsPinching(false);
    setLastPinchDistance(0);
  }, [currentPhotoIndex]);

  // Reset zoom and position when gallery opens
  useEffect(() => {
    if (galleryOpen) {
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
      setIsDragging(false);
      setIsPinching(false);
      setLastPinchDistance(0);
      // Prevent body scroll when gallery is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when gallery is closed
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [galleryOpen]);

  // Zoom functions
  const handleWheel = useCallback((e) => {
    const zoomFactor = 0.1;
    const newZoomLevel = e.deltaY > 0 
      ? Math.max(0.5, zoomLevel - zoomFactor)
      : Math.min(3, zoomLevel + zoomFactor);
    
    setZoomLevel(newZoomLevel);
    
    // Reset position if zooming out to 1x or below
    if (newZoomLevel <= 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  // Pan functions
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
      e.preventDefault();
    }
  }, [zoomLevel, imagePosition]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoomLevel > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, zoomLevel, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch event handlers for pinch-to-zoom
  const getTouchDistance = useCallback((touches) => {
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setIsPinching(true);
      setLastPinchDistance(getTouchDistance(e.touches));
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      // Single finger pan when zoomed
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - imagePosition.x, y: touch.clientY - imagePosition.y });
    }
  }, [getTouchDistance, zoomLevel, imagePosition]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches);
      const scale = currentDistance / lastPinchDistance;
      
      // Apply zoom with limits
      const newZoomLevel = Math.max(0.5, Math.min(3, zoomLevel * scale));
      setZoomLevel(newZoomLevel);
      
      // Reset position if zooming out to 1x or below
      if (newZoomLevel <= 1) {
        setImagePosition({ x: 0, y: 0 });
      }
      
      setLastPinchDistance(currentDistance);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      // Single finger pan when zoomed
      e.preventDefault();
      const touch = e.touches[0];
      setImagePosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  }, [isPinching, getTouchDistance, lastPinchDistance, zoomLevel, isDragging, dragStart]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      setIsPinching(false);
      setIsDragging(false);
      setLastPinchDistance(0);
    } else if (e.touches.length === 1 && isPinching) {
      // Transition from pinch to single touch
      setIsPinching(false);
      if (zoomLevel > 1) {
        setIsDragging(true);
        const touch = e.touches[0];
        setDragStart({ x: touch.clientX - imagePosition.x, y: touch.clientY - imagePosition.y });
      }
    }
  }, [isPinching, zoomLevel, imagePosition]);

  // Add mouse and touch event listeners for panning, zoom and prevent page scroll
  useEffect(() => {
    if (galleryOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Handle wheel events for zoom and prevent page scroll
      const handleWheelZoom = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Only zoom if target is the gallery container or its children
        const galleryContainer = document.querySelector('[data-gallery-zoom-container]');
        if (galleryContainer && (galleryContainer.contains(e.target) || galleryContainer === e.target)) {
          handleWheel(e);
        }
      };
      
      // Touch event handling for gallery container
      const galleryContainer = document.querySelector('[data-gallery-zoom-container]');
      if (galleryContainer) {
        galleryContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
        galleryContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
        galleryContainer.addEventListener('touchend', handleTouchEnd, { passive: false });
      }
      
      // Prevent default touch behaviors on the document during gallery
      const preventDefaultTouch = (e) => {
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('wheel', handleWheelZoom, { passive: false });
      document.addEventListener('touchstart', preventDefaultTouch, { passive: false });
      document.addEventListener('touchmove', preventDefaultTouch, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('wheel', handleWheelZoom);
        document.removeEventListener('touchstart', preventDefaultTouch);
        document.removeEventListener('touchmove', preventDefaultTouch);
        
        if (galleryContainer) {
          galleryContainer.removeEventListener('touchstart', handleTouchStart);
          galleryContainer.removeEventListener('touchmove', handleTouchMove);
          galleryContainer.removeEventListener('touchend', handleTouchEnd);
        }
      };
    }
  }, [galleryOpen, handleMouseMove, handleMouseUp, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeGallery();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'o':
        case 'O':
          openInNewTab();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, closeGallery, goToPrevious, goToNext, openInNewTab]);

  // Handle file selection with auto-resize for oversized photos
  const handleFileSelect = useCallback(async (files) => {
    if (!canEdit) return;
    
    // Sort files by name to maintain sequential order
    const fileArray = Array.from(files).sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    const validFiles = [];
    const errors = [];
    const filesToResize = [];
    const videosToConvert = [];

    // First pass: validate file types and categorize files
    fileArray.forEach((file, index) => {
      console.log('Processing file:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Validate file type - accept images and videos
      if (file.type.startsWith('image/')) {
        console.log('Categorized as image:', file.name);
        // Handle image files
        if (file.size > PHOTO_CONSTANTS.MAX_FILE_SIZE) {
          filesToResize.push({ file, index });
        } else {
          validFiles.push(file);
        }
      } else if (file.type.startsWith('video/')) {
        console.log('Categorized as video for conversion:', file.name);
        // Handle video files for WebM conversion
        videosToConvert.push({ file, index });
      } else {
        console.log('Unsupported file type:', file.name, file.type);
        errors.push(`${file.name}: Tipo de archivo no soportado (solo imágenes y videos)`);
        return;
      }
    });

    // Show initial errors for non-image files
    if (errors.length > 0) {
      setError(errors.join('\n'));
      setTimeout(() => setError(''), 8000);
    }

    // Process files that need resizing
    if (filesToResize.length > 0) {
      try {
        // Set up resize tracking with consistent IDs
        const timestamp = Date.now();
        const resizeItems = filesToResize.map(({ file, index }) => ({
          id: `resize-${timestamp}-${index}`,
          filename: file.name,
          originalSize: file.size,
          progress: 0,
          status: 'resizing'
        }));
        
        setResizingFiles(resizeItems);

        // Resize files
        const resizePromises = filesToResize.map(async ({ file, index }) => {
          const itemId = `resize-${timestamp}-${index}`;
          
          try {
            const resizedFile = await resizeImageFile(file);
            
            // Update progress
            setResizingFiles(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, progress: 100, status: 'completed', newSize: resizedFile.size }
                : item
            ));
            
            return resizedFile;
          } catch (error) {
            console.error(`Error resizing ${file.name}:`, error);
            
            // Update status to error
            setResizingFiles(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, status: 'error', error: error.message }
                : item
            ));
            
            errors.push(`${file.name}: Error al redimensionar (${formatFileSize(file.size)})`);
            return null;
          }
        });

        const resizedFiles = await Promise.all(resizePromises);
        const successfullyResized = resizedFiles.filter(file => file !== null);
        
        // Add successfully resized files to valid files
        validFiles.push(...successfullyResized);
        
        // Clear resize tracking after a delay
        setTimeout(() => setResizingFiles([]), 3000);
        
      } catch (error) {
        console.error('Error during file resizing:', error);
        errors.push('Error durante el redimensionamiento de imágenes');
      }
    }

    // Process videos for WebM conversion
    if (videosToConvert.length > 0) {
      console.log('Processing videos for conversion:', videosToConvert);
      try {
        // Set up conversion tracking
        const timestamp = Date.now();
        const convertItems = videosToConvert.map(({ file, index }) => ({
          id: `convert-${timestamp}-${index}`,
          filename: file.name,
          originalSize: file.size,
          progress: 0,
          status: 'converting'
        }));
        
        setConvertingVideos(convertItems);
        console.log('Set conversion tracking:', convertItems);

        // Convert videos to WebM sequentially (one at a time) to avoid resource contention
        const successfullyConverted = [];
        
        for (let i = 0; i < videosToConvert.length; i++) {
          const { file, index } = videosToConvert[i];
          const itemId = `convert-${timestamp}-${index}`;
          
          try {
            console.log(`Starting sequential conversion ${i + 1}/${videosToConvert.length}: ${file.name}`);
            
            // Update progress to show conversion started
            setConvertingVideos(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, progress: 25, status: 'converting' }
                : item
            ));

            const webmResult = await convertVideoToWebM(file);
            
            // Update progress
            setConvertingVideos(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, progress: 75, status: 'finalizing' }
                : item
            ));

            // Create a File object from the blob
            const convertedFile = new File(
              [webmResult.blob], 
              file.name.replace(/\.[^/.]+$/, '.webm'),
              { type: webmResult.type }
            );

            // Check if resulting file is under 3.2MB limit (slightly higher to accommodate WebM compression variance)
            const maxFileSize = 3.2 * 1024 * 1024; // 3.2MB limit for final file
            if (convertedFile.size > maxFileSize) {
              throw new Error(`Archivo resultante es muy grande (${formatFileSize(convertedFile.size)} > 3.2MB). Intente con un video más corto o de menor resolución.`);
            }

            // Update completion
            setConvertingVideos(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, progress: 100, status: 'completed', newSize: convertedFile.size }
                : item
            ));
            
            successfullyConverted.push(convertedFile);
            console.log(`Completed sequential conversion ${i + 1}/${videosToConvert.length}: ${file.name} (${formatFileSize(convertedFile.size)})`);
            
          } catch (error) {
            console.error(`Error converting ${file.name}:`, error);
            
            // Update status to error
            setConvertingVideos(prev => prev.map(item => 
              item.id === itemId 
                ? { ...item, status: 'error', error: error.message }
                : item
            ));
            
            errors.push(`${file.name}: Error al convertir - ${error.message}`);
            // Don't add to successfullyConverted, just continue with next file
          }
        }
        
        console.log('Conversion completed. Converted files:', successfullyConverted);
        console.log('Successfully converted file details:', successfullyConverted.map(f => ({ name: f.name, size: f.size, type: f.type })));
        console.log('Valid files before adding converted:', validFiles.length);
        
        // Add successfully converted files to valid files
        validFiles.push(...successfullyConverted);
        
        console.log('Valid files after adding converted:', validFiles.length);
        console.log('All valid files for upload:', validFiles.map(f => ({ name: f.name, size: f.size, type: f.type })));
        
        // Clear conversion tracking after a delay
        setTimeout(() => setConvertingVideos([]), 3000);
        
      } catch (error) {
        console.error('Error during video conversion:', error);
        errors.push('Error durante la conversión de videos a WebM');
      }
    }

    // Show any processing errors
    if (errors.length > 0) {
      setError(errors.join('\n'));
      setTimeout(() => setError(''), 8000);
    }

    // Upload all valid files (original + resized + converted)
    console.log('Final valid files for upload:', validFiles);
    if (validFiles.length > 0) {
      console.log('Starting upload of', validFiles.length, 'files');
      handleUpload(validFiles);
    } else {
      console.log('No valid files to upload');
    }
  }, [canEdit, convertVideoToWebM, formatFileSize]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  // Handle file upload
  const handleUpload = async (files) => {
    console.log('handleUpload called with files:', files);
    console.log('orderId:', orderId, 'equipmentId:', equipmentId);
    
    if (!orderId || !equipmentId) {
      setError('orderId y equipmentId son requeridos para subir fotos');
      return;
    }

    try {
      setError('');
      
      // Create upload tracking objects
      const uploadingItems = Array.from(files).map((file, index) => ({
        id: `uploading-${Date.now()}-${index}`,
        file,
        filename: file.name,
        progress: 0,
        preview: URL.createObjectURL(file),
        uploading: true,
        error: null
      }));

      setUploadingFiles(uploadingItems);
      console.log('Upload tracking items created:', uploadingItems);

      // Upload files with progress tracking
      console.log('Starting uploadEquipmentPhotos...');
      const results = await uploadEquipmentPhotos(
        orderId,
        equipmentId,
        files,
        (fileIndex, progress, snapshot) => {
          setUploadingFiles(prev => prev.map((item, index) => 
            index === fileIndex 
              ? { ...item, progress: Math.round(progress) }
              : item
          ));
        }
      );

      // Process upload results
      const successfulUploads = [];
      const uploadErrors = [];

      results.forEach((result, index) => {
        if (result.success) {
          successfulUploads.push({
            id: `photo-${Date.now()}-${index}`,
            photoId: result.photoId,
            downloadURL: result.downloadURL,
            comment: '',
            uploaded: true,
            size: result.size,
            filename: result.filename,
            uploadedAt: result.uploadedAt
          });
        } else {
          uploadErrors.push(`${result.filename}: ${result.error}`);
        }
      });

      // Update photos state using functional update to ensure latest state
      setPhotos(prevPhotos => {
        const newPhotos = [...prevPhotos, ...successfulUploads];
        
        // Defer parent notification to avoid setState during render
        setTimeout(() => {
          if (onPhotosChangeRef.current) {
            onPhotosChangeRef.current(newPhotos);
          }
        }, 0);
        
        return newPhotos;
      });
      
      setHasUserActions(true); // Mark that user has performed actions
      
      // Track newly uploaded photos for potential cleanup on cancel
      setNewlyUploadedPhotos(prev => [...prev, ...successfulUploads]);
      
      // Track ALL uploaded photo IDs for comprehensive cleanup
      const uploadedPhotoIds = successfulUploads.map(photo => photo.photoId);
      setAllUploadedPhotoIds(prev => [...prev, ...uploadedPhotoIds]);

      // Show errors if any
      if (uploadErrors.length > 0) {
        setError(uploadErrors.join('\n'));
        setTimeout(() => setError(''), 8000);
      }

      // Clear uploading files and reset file input
      setUploadingFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clean up preview URLs
      uploadingItems.forEach(item => {
        if (item.preview) {
          URL.revokeObjectURL(item.preview);
        }
      });

    } catch (error) {
      console.error('Upload error:', error);
      setError(`Error durante la subida: ${error.message}`);
      setUploadingFiles([]);
    }
  };

  // Handle photo deletion (mark for deletion, don't delete from storage yet)
  const handleDeletePhoto = (photoIndex) => {
    if (!canEdit) return;

    // Mark photo for deletion instead of actually deleting
    const updatedPhotos = photos.map((photo, index) => 
      index === photoIndex 
        ? { ...photo, markedForDeletion: true }
        : photo
    );
    setPhotos(updatedPhotos);
    setHasUserActions(true); // Mark that user has performed actions
    
    // Notify parent of changes
    if (onPhotosChange) {
      onPhotosChange(updatedPhotos);
    }
  };

  // Handle undo photo deletion
  const handleUndoDelete = (photoIndex) => {
    if (!canEdit) return;

    const updatedPhotos = photos.map((photo, index) => 
      index === photoIndex 
        ? { ...photo, markedForDeletion: false }
        : photo
    );
    setPhotos(updatedPhotos);
    setHasUserActions(true); // Mark that user has performed actions
    
    // Notify parent of changes
    if (onPhotosChange) {
      onPhotosChange(updatedPhotos);
    }
  };

  // Handle comment change
  const handleCommentChange = (photoIndex, comment) => {
    if (!canEdit) return;

    const updatedPhotos = photos.map((photo, index) => 
      index === photoIndex ? { ...photo, comment } : photo
    );
    setPhotos(updatedPhotos);
    setHasUserActions(true); // Mark that user has performed actions
    
    // Notify parent of changes
    if (onPhotosChange) {
      onPhotosChange(updatedPhotos);
    }
  };

  // Handle photo reordering
  const handleDragStart = (e, index) => {
    if (!canEdit) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handlePhotoReorder = (e, dropIndex) => {
    if (!canEdit || draggedIndex === null) return;
    
    e.preventDefault();
    
    const reorderedPhotos = [...photos];
    const draggedPhoto = reorderedPhotos[draggedIndex];
    
    // Remove dragged item
    reorderedPhotos.splice(draggedIndex, 1);
    
    // Insert at new position
    reorderedPhotos.splice(dropIndex, 0, draggedPhoto);
    
    setPhotos(reorderedPhotos);
    setHasUserActions(true); // Mark that user has performed actions
    
    // Notify parent of changes
    if (onPhotosChange) {
      onPhotosChange(reorderedPhotos);
    }
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // File input click handler
  const handleFileInputClick = () => {
    if (canEdit && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <Loader className="h-6 w-6 animate-spin text-purple-600 dark:text-purple-400 mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Cargando fotos existentes...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Camera className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <h4 className="text-lg font-medium text-gray-900 dark:text-white">Fotos del Equipo</h4>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({photos.length} {photos.length === 1 ? 'foto' : 'fotos'})
          </span>
        </div>
        
        {canEdit && (
          <div className="flex items-center space-x-2">
            {!isEditMode ? (
              <button
                onClick={() => setIsEditMode(true)}
                className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded disabled:opacity-50"
                title="Editar fotos"
              >
                <ImagePlus className="h-5 w-5" />
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <Save className="h-4 w-4" />
                  <span>Guardar</span>
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <XCircle className="h-4 w-4" />
                  <span>Cancelar</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700 whitespace-pre-line">{error}</div>
          </div>
        </div>
      )}

      {/* Drag & Drop Upload Area */}
      {canEdit && isEditMode && (
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleFileInputClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
          
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-purple-600 cursor-pointer">
              Haz clic para seleccionar fotos y videos
            </span>{' '}
            o arrastra y suelta aquí
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Fotos: {PHOTO_CONSTANTS.SUPPORTED_FORMATS.join(', ').toUpperCase()} 
            • Máximo {PHOTO_CONSTANTS.MAX_FILE_SIZE_MB}MB
            <br />
            Videos: MP4, MOV, AVI, WebM • Se convierten a video silencioso 30fps (máximo 3MB final)
            <br />
            <span className="text-blue-600">Las fotos grandes se redimensionarán automáticamente</span>
          </p>
        </div>
      )}

      {/* Resizing Files Progress */}
      {resizingFiles.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Redimensionando fotos...</h5>
          {resizingFiles.map((file) => (
            <div key={file.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.filename}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>Original: {formatFileSize(file.originalSize)}</span>
                    {file.newSize && (
                      <>
                        <span>→</span>
                        <span>Reducido: {formatFileSize(file.newSize)}</span>
                      </>
                    )}
                  </div>
                  {file.status === 'resizing' && (
                    <div className="mt-1">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300 animate-pulse"
                          style={{ width: '70%' }}
                        />
                      </div>
                    </div>
                  )}
                  {file.status === 'error' && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{file.error}</p>
                  )}
                </div>
                {file.status === 'completed' && (
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                )}
                {file.status === 'resizing' && (
                  <Loader className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                )}
                {file.status === 'error' && (
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Converting Videos Progress */}
      {convertingVideos.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Convirtiendo videos a WebM silencioso...</h5>
          {convertingVideos.map((file) => (
            <div key={file.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.filename}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                    <span>Original: {formatFileSize(file.originalSize)}</span>
                    {file.newSize && (
                      <>
                        <span>→</span>
                        <span>WebM: {formatFileSize(file.newSize)}</span>
                      </>
                    )}
                  </div>
                  {(file.status === 'converting' || file.status === 'finalizing') && (
                    <div className="mt-1">
                      <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {file.status === 'converting' ? 'Procesando video...' : 'Finalizando WebM...'}
                      </p>
                    </div>
                  )}
                  {file.status === 'error' && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">{file.error}</p>
                  )}
                </div>
                {file.status === 'completed' && (
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                )}
                {(file.status === 'converting' || file.status === 'finalizing') && (
                  <Loader className="h-4 w-4 animate-spin text-purple-600 flex-shrink-0" />
                )}
                {file.status === 'error' && (
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploading Files Progress */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Subiendo fotos...</h5>
          {uploadingFiles.map((file, index) => (
            <div key={file.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                {file.preview && (
                  <img
                    src={file.preview}
                    alt="Preview"
                    className="h-12 w-12 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.filename}
                  </p>
                  <div className="mt-1">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{file.progress}%</p>
                  </div>
                </div>
                <Loader className="h-4 w-4 animate-spin text-purple-600" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Photos Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className={`group bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm transition-transform ${
                canEdit && isEditMode ? 'cursor-move hover:shadow-md' : ''
              } ${draggedIndex === index ? 'opacity-50 scale-95' : ''} ${
                photo.markedForDeletion ? 'opacity-50 border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' : ''
              }`}
              draggable={canEdit && isEditMode && !photo.markedForDeletion}
              onDragStart={isEditMode ? (e) => handleDragStart(e, index) : undefined}
              onDragOver={isEditMode ? handleDragOver : undefined}
              onDrop={isEditMode ? (e) => handlePhotoReorder(e, index) : undefined}
              onDragEnd={isEditMode ? handleDragEnd : undefined}
            >
              {/* Photo/Video Display */}
              <div className="relative aspect-w-16 aspect-h-12">
                {isVideoFile(photo.filename) ? (
                  <div className="relative w-full h-48">
                    <video
                      src={photo.downloadURL}
                      className={`w-full h-full object-cover ${photo.markedForDeletion ? 'grayscale' : ''} ${
                        !isEditMode ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
                      }`}
                      muted
                      preload="metadata"
                      onClick={!isEditMode ? () => openGallery(index) : undefined}
                    />
                    {/* Video indicator overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 group-hover:opacity-0 transition-opacity">
                      <div className="bg-black bg-opacity-60 rounded-full p-2">
                        <Video className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={photo.downloadURL}
                    alt={photo.filename}
                    className={`w-full h-48 object-cover ${photo.markedForDeletion ? 'grayscale' : ''} ${
                      !isEditMode ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
                    }`}
                    loading="lazy"
                    onClick={!isEditMode ? () => openGallery(index) : undefined}
                  />
                )}
                
                
                {/* Gallery Zoom/Play Icon */}
                {!isEditMode && (
                  <div 
                    className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black bg-opacity-30 cursor-pointer"
                    onClick={() => openGallery(index)}
                  >
                    {isVideoFile(photo.filename) ? (
                      <Play className="h-8 w-8 text-white" />
                    ) : (
                      <ZoomIn className="h-8 w-8 text-white" />
                    )}
                  </div>
                )}
                
                {/* Deletion Overlay */}
                {photo.markedForDeletion && (
                  <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                    <div className="text-white font-bold text-lg">ELIMINAR</div>
                  </div>
                )}
                
                {/* Drag Handle */}
                {canEdit && isEditMode && (
                  <div className="absolute top-2 left-2 p-1 bg-black bg-opacity-50 rounded">
                    <GripVertical className="h-4 w-4 text-white" />
                  </div>
                )}
                
                {/* Delete/Undo Button */}
                {canEdit && isEditMode && (
                  <button
                    onClick={() => photo.markedForDeletion ? handleUndoDelete(index) : handleDeletePhoto(index)}
                    className={`absolute top-2 right-2 p-1 text-white rounded transition-colors ${
                      photo.markedForDeletion 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    }`}
                    title={photo.markedForDeletion ? "Deshacer eliminación" : "Eliminar foto"}
                  >
                    {photo.markedForDeletion ? <Undo2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
                  </button>
                )}
                
                {/* Upload Status */}
                {photo.uploaded && (
                  <div className="absolute bottom-2 right-2 p-1 bg-green-500 rounded">
                    <CheckCircle className="h-4 w-4 text-white" />
                  </div>
                )}
              </div>

              {/* Photo Details */}
              <div className="p-4 sm:p-3 space-y-3 sm:space-y-2 bg-white dark:bg-gray-800">
                {/* Comment Field */}
                <div className="space-y-1">
                  <label className="flex items-center text-sm sm:text-xs font-medium text-gray-700 dark:text-gray-300">
                    <MessageSquare className="h-3 w-3 mr-1" />
                    Comentario
                  </label>
                  {isEditMode ? (
                    <textarea
                      value={photo.comment}
                      onChange={(e) => handleCommentChange(index, e.target.value)}
                      placeholder="Agregar comentario..."
                      rows={3}
                      disabled={!canEdit || photo.markedForDeletion}
                      className={`w-full text-sm sm:text-xs border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:px-2 sm:py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-purple-500 dark:focus:border-purple-400 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-500 dark:disabled:text-gray-400 resize-none ${
                        photo.markedForDeletion ? 'opacity-50' : ''
                      }`}
                    />
                  ) : (
                    <div className={`w-full text-sm sm:text-xs border border-gray-200 dark:border-gray-600 rounded px-3 py-2 sm:px-2 sm:py-1 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[3rem] sm:min-h-[2.5rem] whitespace-pre-wrap leading-relaxed ${
                      photo.markedForDeletion ? 'opacity-50' : ''
                    }`}>
                      {photo.comment || <span className="text-gray-400 dark:text-gray-500 italic">Sin comentario</span>}
                    </div>
                  )}
                </div>
                
                {/* Photo/Video Metadata */}
                <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
                  <div className="flex items-center space-x-2">
                    {isVideoFile(photo.filename) && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        <Video className="h-3 w-3 mr-1" />
                        WebM
                      </span>
                    )}
                    {photo.size > 0 && (
                      <div>Tamaño: {formatFileSize(photo.size)}</div>
                    )}
                  </div>
                  {photo.uploadedAt && (
                    <div>
                      Subida: {new Date(photo.uploadedAt).toLocaleDateString('es-ES')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {photos.length === 0 && uploadingFiles.length === 0 && !loading && (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <Camera className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No hay fotos subidas para este equipo
          </p>
          {canEdit && isEditMode && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Haz clic en el área punteada o arrastra archivos aquí
            </p>
          )}
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryOpen && photos.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
          {/* Top Right Controls */}
          <div className="absolute top-4 right-4 z-10 flex space-x-2">
            <button
              onClick={openInNewTab}
              className="p-2 text-white hover:text-gray-300 transition-colors"
              title="Abrir en nueva pestaña"
            >
              <ExternalLink className="h-6 w-6" />
            </button>
            <button
              onClick={closeGallery}
              className="p-2 text-white hover:text-gray-300 transition-colors"
              title="Cerrar galería"
            >
              <X className="h-8 w-8" />
            </button>
          </div>

          {/* Navigation Buttons */}
          {photos.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white hover:text-gray-300 transition-colors"
              >
                <ChevronLeft className="h-12 w-12" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 text-white hover:text-gray-300 transition-colors"
              >
                <ChevronRight className="h-12 w-12" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div 
            className="flex items-center justify-center w-full h-full p-8"
            data-gallery-zoom-container
            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
              {isVideoFile(photos[currentPhotoIndex]?.filename) ? (
                <video
                  ref={galleryVideoRef}
                  src={photos[currentPhotoIndex]?.downloadURL}
                  className="max-w-full max-h-full object-contain transition-transform duration-200"
                  style={{ 
                    maxHeight: 'calc(100vh - 200px)', 
                    maxWidth: 'calc(100vw - 100px)',
                    transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                    transformOrigin: 'center center'
                  }}
                  muted
                  controls
                  onMouseDown={handleMouseDown}
                  draggable={false}
                />
              ) : (
                <img
                  src={photos[currentPhotoIndex]?.downloadURL}
                  alt={photos[currentPhotoIndex]?.filename}
                  className="max-w-full max-h-full object-contain transition-transform duration-200"
                  style={{ 
                    maxHeight: 'calc(100vh - 200px)', 
                    maxWidth: 'calc(100vw - 100px)',
                    transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                    transformOrigin: 'center center'
                  }}
                  onMouseDown={handleMouseDown}
                  draggable={false}
                />
              )}
            </div>
          </div>

          {/* Photo Info */}
          <div className={`absolute left-4 right-4 text-white ${photos.length > 1 ? 'bottom-32 sm:bottom-28' : 'bottom-4'}`}>
            <div className="bg-black bg-opacity-50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <div className="flex-1">
                  {photos[currentPhotoIndex]?.comment && (
                    <p className="text-sm text-gray-300 mb-2 max-h-16 sm:max-h-none overflow-hidden leading-relaxed">
                      {photos[currentPhotoIndex].comment}
                    </p>
                  )}
                  <div className="text-xs text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
                    {photos[currentPhotoIndex]?.size > 0 && (
                      <span>Tamaño: {formatFileSize(photos[currentPhotoIndex].size)}</span>
                    )}
                    {photos[currentPhotoIndex]?.uploadedAt && (
                      <span>
                        Subida: {new Date(photos[currentPhotoIndex].uploadedAt).toLocaleDateString('es-ES')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm">
                  {photos.length > 1 && (
                    <div className="text-gray-300">
                      {currentPhotoIndex + 1} de {photos.length}
                    </div>
                  )}
                  <div className="text-gray-300">
                    Zoom: {Math.round(zoomLevel * 100)}%
                  </div>
                  <div className="text-xs text-gray-400">
                    <span className="hidden sm:block">
                      {zoomLevel === 1 ? 'Rueda del ratón para zoom' : 'Arrastra para mover'}
                    </span>
                    <span className="block sm:hidden">
                      {zoomLevel === 1 ? 'Pellizca para zoom' : 'Arrastra para mover'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnail Strip */}
          {photos.length > 1 && (
            <div className="absolute bottom-4 left-4 right-4">
              <div className="flex justify-center space-x-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
                {photos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => setCurrentPhotoIndex(index)}
                    className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                      index === currentPhotoIndex ? 'border-white' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <div className="relative w-full h-full">
                      {isVideoFile(photo.filename) ? (
                        <video
                          src={photo.downloadURL}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                      ) : (
                        <img
                          src={photo.downloadURL}
                          alt={photo.filename}
                          className="w-full h-full object-cover"
                        />
                      )}
                      
                      {/* Video indicator for thumbnails */}
                      {isVideoFile(photo.filename) && (
                        <div className="absolute bottom-0.5 right-0.5 sm:bottom-1 sm:right-1 bg-black bg-opacity-60 rounded p-0.5">
                          <Video className="h-1.5 w-1.5 sm:h-2 sm:w-2 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentPhotoUpload;